'use client';
import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Trash2, Plus, Edit } from 'lucide-react';

const ModalExclusao = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 animate-fade-in-scale">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Confirmar Exclusão</h2>
            <p className="text-slate-600">
                Tem a certeza que deseja excluir este utilizador? Esta ação não poderá ser desfeita.
            </p>
            <div className="mt-6 flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold"
                >
                    Cancelar
                </button>
                <button
                    onClick={onConfirm}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                >
                    Excluir
                </button>
            </div>
        </div>
    </div>
);



const ModalUsuario = ({ isOpen, onClose, onSave, usuario, grupo }) => { 
    const [dadosFormulario, setDadosFormulario] = useState({
        name: '',
        lastname: '',
        email: '',
        telefone: '',
        data_nascimento: '',
        cerimoniario: false,
        ano_investidura: '',
    });
    const [telefoneValido, setTelefoneValido] = useState(true);
    const [erroEmail, setErroEmail] = useState('');
    const [usuariosGrupo, setUsuariosGrupo] = useState([]);

    useEffect(() => {
        async function fetchUsuariosGrupo() {
            if (grupo?.grupo_id) {
                try {
                    const res = await api.get(`/usuarios/grupo/${grupo.grupo_id}`);
                    setUsuariosGrupo(res.data || []);
                } catch {
                    setUsuariosGrupo([]);
                }
            } else {
                setUsuariosGrupo([]);
            }
        }
        fetchUsuariosGrupo();
    }, [grupo?.grupo_id, isOpen]);

    useEffect(() => {
        if (usuario) { 
            // Garante que a data seja exibida no formato yyyy-mm-dd (UTC)
            let dataNascimento = '';
            if (usuario.data_nascimento) {
                const date = new Date(usuario.data_nascimento);
                if (!isNaN(date.getTime())) {
                    // Extrai a data em UTC
                    const ano = date.getUTCFullYear();
                    const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
                    const dia = String(date.getUTCDate()).padStart(2, '0');
                    dataNascimento = `${ano}-${mes}-${dia}`;
                }
            }
            setDadosFormulario({
                name: usuario.name || '',
                lastname: usuario.lastname || '',
                email: usuario.email || '',
                telefone: usuario.telefone || '',
                data_nascimento: dataNascimento,
                cerimoniario: !!usuario.cerimoniario,
                ano_investidura: usuario.ano_investidura ?? '',
            });
        } else { 
            setDadosFormulario({
                name: '', lastname: '', email: '', telefone: '', data_nascimento: '', cerimoniario: false, ano_investidura: '',
            });
        }
        setErroEmail('');
    }, [usuario]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let novoValor = type === 'checkbox' ? checked : value;
        // Máscara para telefone (formato brasileiro: (99) 99999-9999)
        if (name === 'telefone') {
            novoValor = value.replace(/\D/g, '');
            if (novoValor.length > 11) novoValor = novoValor.slice(0, 11);
            if (novoValor.length > 0) {
                novoValor = novoValor.replace(/(\d{2})(\d)/, '($1) $2');
                novoValor = novoValor.replace(/(\d{5})(\d{4})$/, '$1-$2');
            }
            setTelefoneValido(/^(\(\d{2}\) )?\d{4,5}-\d{4}$/.test(novoValor));
        }
        setDadosFormulario({ ...dadosFormulario, [name]: novoValor });
        if (name === 'email') setErroEmail('');
    };

    const showCerimoniarioToggle = (
        (usuario?.tipo && String(usuario.tipo).toLowerCase().includes('acolitos')) ||
        (grupo?.tipo && String(grupo.tipo).toLowerCase().includes('acolitos')) ||
        ((grupo?.nome || grupo?.name || grupo?.grupo_name || grupo?.grupo_nome) &&
            String(grupo.nome || grupo.name || grupo.grupo_name || grupo.grupo_nome).toLowerCase().includes('acolitos'))
    );

    const internalHandleSave = () => {
        if (!grupo) {
            alert("Erro: Dados do grupo não encontrados.");
            return;
        }
        // Validação de email duplicado (ignora o próprio email em edição)
        const emailLower = dadosFormulario.email.trim().toLowerCase();
        const emailDuplicado = usuariosGrupo.some(u => u.email && u.email.toLowerCase() === emailLower && (!usuario || u.id !== usuario.id));
        if (emailDuplicado) {
            setErroEmail('Já existe um usuário com este email neste grupo.');
            return;
        }
        // Validação de telefone
        if (dadosFormulario.telefone && !telefoneValido) {
            alert('Telefone inválido. Use o formato (99) 99999-9999.');
            return;
        }
        // Garante que a data de nascimento seja salva em UTC (yyyy-mm-ddT00:00:00Z)
        let dataNascimentoUTC = dadosFormulario.data_nascimento;
        if (dataNascimentoUTC) {
            // Se vier só yyyy-mm-dd, transforma para UTC
            if (/^\d{4}-\d{2}-\d{2}$/.test(dataNascimentoUTC)) {
                dataNascimentoUTC = `${dataNascimentoUTC}T00:00:00Z`;
            }
        }
        const payload = {
            ...dadosFormulario,
            data_nascimento: dataNascimentoUTC,
            paroquia_id: grupo.paroquia_id,
            comunidade_id: grupo.comunidade_id,
            grupo_id: grupo.grupo_id,
            cerimoniario: dadosFormulario.cerimoniario ? 1 : 0,
            ano_investidura: dadosFormulario.ano_investidura ? Number(dadosFormulario.ano_investidura) : null,
        };
        onSave(payload, usuario?.id);
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40 p-4" aria-modal="true" role="dialog">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-xl font-bold mb-4">
                    {usuario ? 'Editar Utilizador' : 'Novo Utilizador'}
                </h2>
                <form onSubmit={(e) => { e.preventDefault(); internalHandleSave(); }} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="name" placeholder="Nome" value={dadosFormulario.name} onChange={handleChange} className="w-full p-3 border border-slate-300 rounded-lg" required />
                        <input type="text" name="lastname" placeholder="Sobrenome" value={dadosFormulario.lastname} onChange={handleChange} className="w-full p-3 border border-slate-300 rounded-lg" required />
                    </div>
                    <input type="email" name="email" placeholder="Email" value={dadosFormulario.email} onChange={handleChange} className={`w-full p-3 border border-slate-300 rounded-lg ${erroEmail ? 'border-red-500' : ''}`} required />
                    {erroEmail && <div className="text-red-500 text-sm mt-1">{erroEmail}</div>}
                    {/* Campos que só aparecem na edição */}
                    {usuario && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <input type="tel" name="telefone" placeholder="Telefone" value={dadosFormulario.telefone} onChange={handleChange} className={`w-full p-3 border border-slate-300 rounded-lg ${dadosFormulario.telefone && !telefoneValido ? 'border-red-500' : ''}`} />
                            {dadosFormulario.telefone && !telefoneValido && <div className="text-red-500 text-sm mt-1">Telefone inválido</div>}
                            <input type="date" name="data_nascimento" value={dadosFormulario.data_nascimento} onChange={handleChange} className="w-full p-3 border border-slate-300 rounded-lg" />
                            {showCerimoniarioToggle && (
                                <input type="number" name="ano_investidura" placeholder="Ano de Investidura" value={dadosFormulario.ano_investidura} onChange={handleChange} className="w-full p-3 border border-slate-300 rounded-lg" min="1900" max="2100" />
                            )}
                        </div>
                    )}

                    {showCerimoniarioToggle && (
                        <div className="mt-3 flex items-center gap-4">
                            <span className="text-sm font-medium text-slate-700">Cerimoniário</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" name="cerimoniario" checked={!!dadosFormulario.cerimoniario} onChange={handleChange} className="sr-only" />
                                <div className={`w-11 h-6 rounded-full transition-colors ${dadosFormulario.cerimoniario ? 'bg-indigo-600' : 'bg-slate-300'}`}></div>
                                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${dadosFormulario.cerimoniario ? 'translate-x-5' : ''}`}></div>
                            </label>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold">
                            Cancelar
                        </button>
                        <button type="submit" className="px-5 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700">
                            Salvar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

function GerenciadorUsuarios({ grupo, usuarioPainel, token }) {
    // (logs removidos)

    const [usuarios, setUsuarios] = useState([]);
    const [pesquisa, setPesquisa] = useState('');
    const [modalAberto, setModalAberto] = useState(false);
    const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
    const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
    const [idParaExcluir, setIdParaExcluir] = useState(null);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);

    // Evita recarregamento desnecessário
    useEffect(() => {
        const abortController = new AbortController();
        async function carregarDados() {
            setLoading(true);
            setErro(null);
            try {
                if (!grupo?.grupo_id) {
                    setUsuarios([]);
                    setLoading(false);
                    return;
                }
                const resUsuarios = await api.get(`/usuarios/grupo/${grupo.grupo_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: abortController.signal
                });
                setUsuarios(resUsuarios.data);
            } catch (err) {
                if (err.name === 'CanceledError' || err.name === 'AbortError') {
                    // requisição cancelada
                } else if (err.response?.status === 401) setErro('Sessão expirada. Faça login novamente.');
                else setErro('Erro ao carregar usuários.');
            } finally {
                setLoading(false);
            }
        }
        carregarDados();
        return () => { abortController.abort(); };
    }, [grupo?.grupo_id, token]);

    const abrirModal = useCallback((tipo, usuario = null) => {
        setModalAberto(true);
        setUsuarioSelecionado(usuario);
        setErro(null);
    }, []);

    const fecharModal = useCallback(() => {
        setModalAberto(false);
        setUsuarioSelecionado(null);
        setErro(null);
    }, []);

    const [toast, setToast] = useState(null);

    const showInvestiduraColumn = (
        (grupo?.tipo && String(grupo.tipo).toLowerCase().includes('acolitos')) ||
        ((grupo?.nome || grupo?.name || grupo?.grupo_name || grupo?.grupo_nome) &&
            String(grupo.nome || grupo.name || grupo.grupo_name || grupo.grupo_nome).toLowerCase().includes('acolitos'))
    );
    const salvarUsuario = useCallback(async (payload, id) => {
        setErro(null);
        try {
            let savedUser;
            if (id) {
                const res = await api.put(`/usuarios/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                savedUser = res.data;
                setUsuarios(prev => prev.map(u => u.id === id ? { ...u, ...savedUser } : u));
            } else {
                const res = await api.post('/usuarios', payload, { headers: { Authorization: `Bearer ${token}` } });
                savedUser = res.data;
                setUsuarios(prev => [savedUser, ...prev]);
            }
            fecharModal();
            setToast('Usuário salvo com sucesso!');
            setTimeout(() => setToast(null), 2500);

            // Refetch silencioso em background para manter sincronia/ordenação
            api.get(`/usuarios/grupo/${grupo.grupo_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(resUsuarios => {
                setUsuarios(resUsuarios.data);
            }).catch(err => console.error("Erro no fetch silencioso de usuários:", err));

        } catch (err) {
            if (err.response?.status === 401) setErro('Sessão expirada. Faça login novamente.');
            else {
                setErro('Ocorreu um erro ao salvar o utilizador.');
            }
        }
    }, [token, grupo?.grupo_id, fecharModal]);
    
    const confirmarExclusao = useCallback((id) => {
        setIdParaExcluir(id);
        setModalExcluirAberto(true);
        setErro(null);
    }, []);

    const excluirConfirmado = useCallback(async () => {
        setErro(null);
        try {
            await api.delete(`/usuarios/${idParaExcluir}`, { headers: { Authorization: `Bearer ${token}` } });
            setUsuarios(prev => prev.filter(u => u.id !== idParaExcluir));
            setModalExcluirAberto(false);
            setIdParaExcluir(null);
            setToast('Usuário excluído com sucesso!');
            setTimeout(() => setToast(null), 2500);

            // Refetch silencioso em background
            api.get(`/usuarios/grupo/${grupo.grupo_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(resUsuarios => {
                setUsuarios(resUsuarios.data);
            }).catch(err => console.error("Erro no fetch silencioso de usuários:", err));

        } catch (err) {
            if (err.response?.status === 401) setErro('Sessão expirada. Faça login novamente.');
            else {
                setErro('Ocorreu um erro ao excluir o utilizador.');
            }
        }
    }, [token, grupo?.grupo_id, idParaExcluir]);


    const formatarData = (dataString) => {
      if (!dataString) return 'N/A';
      // Aceita ISO, UTC, ou yyyy-mm-dd
      let date;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dataString)) {
        date = new Date(dataString + 'T00:00:00Z');
      } else {
        date = new Date(dataString);
      }
      if (isNaN(date.getTime())) return 'N/A';
      // Formata em UTC
      const dia = String(date.getUTCDate()).padStart(2, '0');
      const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
      const ano = date.getUTCFullYear();
      return `${dia}/${mes}/${ano}`;
    };

    // Filtra usuários conforme pesquisa
    const usuariosFiltrados = usuarios.filter(u => {
        const termo = pesquisa.trim().toLowerCase();
        if (!termo) return true;
        const nomeCompleto = `${u.name || ''} ${u.lastname || ''}`.trim().toLowerCase();
        return (
            nomeCompleto.includes(termo) ||
            (u.name && u.name.toLowerCase().includes(termo)) ||
            (u.lastname && u.lastname.toLowerCase().includes(termo)) ||
            (u.email && u.email.toLowerCase().includes(termo))
        );
    });

    return (
        <div className="font-sans">
            <style>{`.animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; } @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
            {erro && <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg">{erro}</div>}
            {toast && <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in-scale">{toast}</div>}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-2">
                <h1 className="text-2xl font-bold text-slate-800">Usuários</h1>
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                    <input
                        type="text"
                        value={pesquisa}
                        onChange={e => setPesquisa(e.target.value)}
                        placeholder="Pesquisar por nome, sobrenome ou email..."
                        className="w-full sm:w-64 p-2 border border-slate-300 rounded-lg"
                        style={{ minWidth: '180px' }}
                    />
                    <button
                        onClick={() => abrirModal('criar')}
                        className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        disabled={!grupo?.grupo_id || loading}
                    >
                        <Plus size={20} />
                        Novo Utilizador
                    </button>
                </div>
            </div>
            <div className="mb-4 text-slate-600 font-semibold">Total de usuários: {usuariosFiltrados.length}</div>
            {loading ? <p className="text-center text-slate-500">A carregar...</p> : (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm hidden md:table">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Nome</th>
                                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Email</th>
                                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Telefone</th>
                                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Nascimento</th>
                                    {showInvestiduraColumn && (
                                        <th className="px-4 py-3 text-left font-semibold text-slate-600">Investidura</th>
                                    )}
                                    <th className="px-4 py-3 text-right font-semibold text-slate-600">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {usuariosFiltrados.map((usuario) => (
                                    <tr key={usuario.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 font-medium text-slate-900">{usuario.name} {usuario.lastname}</td>
                                        <td className="px-4 py-3 text-slate-600">{usuario.email}</td>
                                        <td className="px-4 py-3 text-slate-600">{usuario.telefone || '-'}</td>
                                        <td className="px-4 py-3 text-slate-600">{formatarData(usuario.data_nascimento)}</td>
                                        {showInvestiduraColumn && (
                                            <td className="px-4 py-3 text-slate-600">{usuario.ano_investidura ?? '-'}</td>
                                        )}
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => abrirModal('editar', usuario)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" disabled={loading}><Edit size={16} /></button>
                                                <button onClick={() => confirmarExclusao(usuario.id)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md" disabled={loading}><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="md:hidden divide-y divide-slate-200">
                           {usuariosFiltrados.map((usuario) => (
                               <div key={usuario.id} className="p-4">
                                   <div className="flex justify-between items-center">
                                       <p className="font-bold text-slate-800">{usuario.name} {usuario.lastname}</p>
                                        <div className="flex gap-2">
                                            <button onClick={() => abrirModal('editar', usuario)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" disabled={loading}><Edit size={16} /></button>
                                            <button onClick={() => confirmarExclusao(usuario.id)} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md" disabled={loading}><Trash2 size={16} /></button>
                                        </div>
                                   </div>
                                   <p className="text-sm text-slate-600">{usuario.email}</p>
                                   <p className="text-sm text-slate-600 mt-1">Telefone: {usuario.telefone || '-'}</p>
                                   <p className="text-sm text-slate-600 mt-1">Nascimento: {formatarData(usuario.data_nascimento)}</p>
                                   {showInvestiduraColumn && (
                                       <p className="text-sm text-slate-600 mt-1">Investidura: {usuario.ano_investidura ?? '-'}</p>
                                   )}
                               </div>
                           ))}
                        </div>
                        {usuariosFiltrados.length === 0 && !loading && (
                            <div className="text-center py-16 px-6">
                                <h3 className="text-lg font-semibold text-slate-700">Nenhum utilizador encontrado</h3>
                                <p className="text-slate-500 mt-2">Clique em "Novo Utilizador" para adicionar membros ao seu grupo.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <ModalUsuario 
                isOpen={modalAberto} 
                onClose={fecharModal}
                onSave={salvarUsuario}
                usuario={usuarioSelecionado}
                grupo={grupo}
            />
            {modalExcluirAberto && (
                <ModalExclusao 
                    onConfirm={excluirConfirmado}
                    onCancel={() => setModalExcluirAberto(false)}
                />
            )}
        </div>
    );
}

export default GerenciadorUsuarios;